import { Component, type ErrorInfo, type JSX, type ReactNode } from 'react';

import { ErrorPage } from '@/shared/ui';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * The one class component in the codebase (CLAUDE.md §7.2), and the last net
 * under everything: it catches what fails *outside* the router — a provider, a
 * store, the router's own construction — where `RouteErrorBoundary` cannot
 * reach. Both render the same screen, so which of the two caught it is an
 * implementation detail nobody outside this folder can see.
 *
 * It never renders the error text itself — that would risk leaking a message
 * containing patient data into the UI (CLAUDE.md §3.8). No «صفحهٔ اصلی» link
 * either: if the shell is what broke, home is this same page.
 */
export class AppErrorBoundary extends Component<Props, State> {
  public override state: State = { hasError: false };

  public static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  public override componentDidCatch(error: Error, info: ErrorInfo): void {
    // wire to the error tracker here; never include request payloads
    void error;
    void info;
  }

  public override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorPage
          kind="crash"
          onRetry={(): void => {
            window.location.reload();
          }}
        />
      );
    }

    return this.props.children as JSX.Element;
  }
}
